import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import StatsCounter from "./StatsCounter";

const SearchForm = ({ sports }) => {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);

  // Update flag on resize
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [filters, setFilters] = useState({
    sport: null,
    course: "",
    location: [],
    budget: "",
  });

  const navigate = useNavigate();

  const groupLocationsByDistrictState = (locations) => {
    const groupedLocations = {};

    locations.forEach((location) => {
      const key = `${location.district}, ${location.state}`;
      if (!groupedLocations[key]) {
        groupedLocations[key] = {
          districtState: key,
          location_ids: []
        };
      }
      groupedLocations[key].location_ids.push(location.location_id);
    });

    return Object.values(groupedLocations);
  };

  const handleSportChange = (e) => {
    const sportId = e.target.value;
    const selected = sports.find((sport) => sport.sport_id === parseInt(sportId, 10));
    setFilters((prev) => ({
      ...prev,
      sport: selected,
      course: "",
      location: [],
    }));
  };

  const handleCourseChange = (e) => {
    setFilters((prev) => ({ ...prev, course: parseInt(e.target.value, 10) }));
  };


  const handleLocationChange = (e) => {
    const locationIds = e.target.value
      ? e.target.value.split(',').map(id => parseInt(id, 10))
      : [];

    const numericIds = locationIds.map(id => parseInt(id, 10));
    setFilters((prev) => ({
      ...prev,
      location: numericIds
    }));
  };




  const handleBudgetChange = (e) => {
    setFilters((prev) => ({ ...prev, budget: e.target.value }));
  };

  const handleSearch = () => {
    const { sport, course, location, budget } = filters;

    if (!sport && !course && location.length === 0 && !budget) {
      alert("Please select at least one filter (Sport, Course, Location, or Budget).");
      return;
    }

    // Construct URL parameters
    const params = new URLSearchParams();


    if (sport) {
      params.append('sport', sport.sport_id); // Force comma format
    }

    if (course) {
      params.append('course', course); //course is now COURSE ID
    }

    if (location.length > 0) {
      params.append('location', location.join(','));
    }


    if (budget) {
      params.append('budget', budget);
    }

    navigate(`/search-results?${params.toString()}`);
  };

  const getFilteredLocations = () => {
    const locations = filters.sport
      ? filters.sport.locations
      : sports?.flatMap((sport) => sport.locations) || [];

    return groupLocationsByDistrictState(locations);
  };

  return (
    <section id="searchForm" className="relative h-[50vh] md:h-[70vh]">
      {isDesktop ? (
        <video
          className="absolute inset-0 w-full h-full object-cover z-0"
          loop
          muted
          autoPlay
          playsInline
          preload="metadata"
          poster="https://res.cloudinary.com/priyagrover0018/image/upload/v1751740418/Screenshot_2025-07-06_000041_chwvnf.png"
        >
          <source
            src="https://res.cloudinary.com/priyagrover0018/video/upload/f_auto,q_auto,w_720/v1751276822/2024_papsura_ambient_HD_h264_16x9_w_ybbuki.mp4"
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>
      ) : (
        <img
          src="https://res.cloudinary.com/priyagrover0018/image/upload/v1751740418/Screenshot_2025-07-06_000041_chwvnf.png"
          alt="Adventure background"
          className="absolute inset-0 w-full h-full object-cover z-0"
          loading="eager"
          decoding="async"
        />
      )}

      <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/40 to-neutral-900/20 z-10"></div>

      <div className="relative z-20 container mx-auto px-4 md:px-6 pt-8 md:pt-12 pb-6 h-full flex items-center justify-center">
        <div className="flex flex-col items-center justify-center text-center text-white w-full px-2">
          <h1 className="text-2xl md:text-4xl lg:text-6xl font-bold mb-2 md:mb-4 animate__animated animate__fadeIn">
            Unleash the Explorer in You
          </h1>
          <p className="text-sm md:text-lg mb-6 md:mb-12 animate__animated animate__fadeIn animate__delay-1s">
            Adventure Awaits Beyond the Horizon!
          </p>

          <div className="w-full max-w-full sm:max-w-5xl bg-white/10 backdrop-blur-md rounded-lg sm:rounded-full p-1 sm:p-1 flex flex-col sm:flex-row gap-2 animate__animated animate__fadeInUp animate__delay-2s">
            <div className="w-full sm:w-auto sm:flex-1 relative flex items-center">
              <select
                id="sportDropdown"
                className="w-full appearance-none bg-transparent text-white rounded-full px-4 py-2 md:py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:bg-gray-500 text-sm"
                onChange={handleSportChange}
                value={filters.sport ? filters.sport.sport_id : ""}
              >
                <option value="">Select Sport</option>
                {sports?.map((sport) => (
                  <option key={sport.sport_id} value={sport.sport_id}>
                    {sport.name}
                  </option>
                ))}
              </select>
              <span className="text-white mx-2 hidden sm:inline">|</span>
            </div>

            {filters.sport?.courses?.length > 0 && (
              <div className="w-full sm:w-auto sm:flex-1 relative flex items-center">
                <select
                  id="courseDropdown"
                  className="w-full appearance-none bg-transparent text-white rounded-full px-4 py-2 md:py-1.5 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:bg-gray-500 text-sm"
                  onChange={handleCourseChange}
                  value={filters.course}
                >
                  <option value="">Select Course</option>
                  {filters.sport.courses.map((course) => (
                    <option key={course.course_id} value={course.course_id}>
                      {course.name}
                    </option>
                  ))}
                </select>
                <span className="text-white mx-2 hidden sm:inline">|</span>
              </div>
            )}

            <div className="w-full sm:w-auto sm:flex-1 relative flex items-center">
              <select
                id="locationDropdown"
                className="w-full appearance-none bg-transparent text-white rounded-full px-4 py-2 md:py-1.5 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:bg-gray-500 text-sm"
                onChange={handleLocationChange}
                value={filters.location.length > 0 ? filters.location.join(',') : ""}
              >
                <option value="">Select Location</option>
                {getFilteredLocations().map((locationGroup) => (
                  <option
                    key={locationGroup.districtState}
                    value={locationGroup.location_ids.join(',')}
                  >
                    {locationGroup.districtState}
                  </option>

                ))}
              </select>
              <span className="text-white mx-2 hidden sm:inline">|</span>
            </div>

            <div className="w-full sm:w-auto sm:flex-1 relative flex items-center">
              <input
                type="number"
                id="budgetInput"
                className="w-full appearance-none bg-transparent text-white rounded-full px-4 py-2 md:py-1.5 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:bg-gray-500 placeholder:text-white text-sm"
                placeholder="Enter Max Price"
                value={filters.budget}
                onChange={handleBudgetChange}
              />
              <span className="text-white mx-2 hidden sm:inline">|</span>
            </div>

            <button
              id="searchButton"
              className="w-full sm:w-auto bg-white text-black font-semibold rounded-full px-6 py-2 md:py-1.5 hover:bg-gray-100 transition duration-300 text-sm"
              onClick={handleSearch}
            >
              Search
            </button>
          </div>

          <div className="hidden md:block">
            <StatsCounter />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SearchForm;